import React, { useEffect, useRef } from 'react';
import { calculatePanApi, chatApi, type PanCalculateOptions, type PanCalculateResponse } from '../lib/api';

const CHANNEL = 'zhengyuan-report-channel';

interface ZhengyuanSession {
  gender: string;
  panSummary: string;
  sourceDateTime: string;
  mode: 'hour' | 'ke';
  hookText: string;
  dimensionScores: QimenDimensionScore[];
  overallScore: number;
  gradeText: string;
  compatibilityPercent: string;
  stabilityText: string;
}

interface PanPalaceCell {
  position: number;
  trigram: string;
  deitySymbol: string;
  gateSymbol: string;
  starSymbol: string;
  heavenlyStem: string;
  earthlyStem: string;
  hasVoid: boolean;
  hasTomb: boolean;
  hasXing: boolean;
  hasMenPo: boolean;
  isHorse: boolean;
  isZhiFu: boolean;
  isZhiShi: boolean;
}

interface QimenDimensionScore {
  key: string;
  label: string;
  score: number;
  direction: 'positive' | 'risk';
  basis: string;
}

const ZHENG_YUAN_REPORT_HTML = String.raw`<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>奇门遁甲 - 灵魂伴侣</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@600;700;900&display=swap');

        :root {
            --primary-gradient: linear-gradient(135deg, #a5b4fc 0%, #f0abfc 100%);
            --accent-glow: 0 0 20px rgba(165, 180, 252, 0.4);
            --bg-deep: #050508;
            --accent-color: #f0abfc;
        }

        body {
            font-family: 'Noto Serif SC', 'STKaiti', 'KaiTi', serif;
            background-color: var(--bg-deep);
            color: #f8fafc;
            background-image:
                radial-gradient(circle at 10% 20%, rgba(165, 180, 252, 0.05) 0%, transparent 40%),
                radial-gradient(circle at 90% 80%, rgba(240, 171, 252, 0.05) 0%, transparent 40%);
            min-height: 100vh;
            overflow-x: hidden;
        }

        .fade-in { animation: fadeIn 1s cubic-bezier(0.4, 0, 0.2, 1) forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }

        .gradient-text {
            background: var(--primary-gradient);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            font-weight: 900;
        }

        .abstract-portrait {
            width: 180px;
            height: 180px;
            margin: 0 auto;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .energy-core {
            width: 70px;
            height: 70px;
            background: var(--primary-gradient);
            border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%;
            filter: blur(20px);
            animation: morph 12s ease-in-out infinite alternate;
            opacity: 0.8;
            box-shadow: var(--accent-glow);
        }

        @keyframes morph {
            0% { border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; transform: rotate(0deg) scale(1); }
            100% { border-radius: 70% 30% 50% 50% / 30% 60% 40% 70%; transform: rotate(360deg) scale(1.4); }
        }

        .btn-main {
            background: var(--primary-gradient);
            color: #050508;
            font-weight: 900;
            transition: all 0.4s;
            position: relative;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(165, 180, 252, 0.3);
            border: none;
        }

        .btn-main::after {
            content: '';
            position: absolute;
            top: -50%; left: -50%;
            width: 200%; height: 200%;
            background: linear-gradient(transparent, rgba(255,255,255,0.4), transparent);
            transform: rotate(45deg);
            animation: sweep 3s infinite;
        }

        @keyframes sweep { 0% { left: -120%; } 100% { left: 120%; } }

        .report-card {
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.06);
            backdrop-filter: blur(25px);
            border-radius: 28px;
            padding: 1.45rem 1.05rem;
            margin-bottom: 1.15rem;
            position: relative;
        }

        .section-header {
            position: relative;
            margin-bottom: 1.1rem;
            padding-bottom: 0.5rem;
        }

        .section-header h2 {
            font-size: 1.5rem;
            font-weight: 900;
            background: var(--primary-gradient);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            letter-spacing: 0.15em;
            display: inline-block;
        }

        .section-header::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 60px;
            height: 3px;
            background: var(--primary-gradient);
            border-radius: 10px;
            box-shadow: var(--accent-glow);
        }

        .content-list {
            margin: 1.2rem 0;
        }

        .content-item {
            position: relative;
            padding-left: 1.6rem;
            margin-bottom: 1rem;
            line-height: 1.8;
            color: #cbd5e1;
        }

        .content-item::before {
            content: '✦';
            position: absolute;
            left: 0;
            color: #a5b4fc;
            font-size: 0.85rem;
            top: 0.1rem;
        }

        .report-text {
            line-height: 1.58;
            color: #cbd5e1;
            font-size: clamp(0.88rem, 2.55vw, 0.96rem);
            text-align: justify;
        }

        .report-text ul,
        .report-text ol {
            margin: 0.8rem 0 1rem 1.4rem;
            line-height: 1.9;
        }

        .report-text ul { list-style: disc; }
        .report-text ol { list-style: decimal; }
        .report-text li { margin: 0.35rem 0; }

        .report-text b {
            background: var(--primary-gradient);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            font-weight: 900;
        }

        .sentence-line {
            margin: 0.58rem 0;
            line-height: 1.58;
        }

        .section-subtitle {
            margin-top: -0.8rem;
            margin-bottom: 1rem;
            color: #94a3b8;
            font-size: 0.78rem;
            letter-spacing: 0.2em;
            text-transform: uppercase;
            font-weight: 700;
        }

        .sub-heading {
            color: #a5b4fc;
            font-weight: 900;
            margin: 1.1rem 0 0.45rem 0;
            font-size: 0.98rem;
            border-left: 3px solid #a5b4fc;
            padding-left: 9px;
        }

        .metric-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.7rem;
            margin: 0.8rem 0 0.9rem;
        }

        .metric-item {
            background: rgba(165, 180, 252, 0.04);
            border: 1px solid rgba(165, 180, 252, 0.1);
            padding: 0.75rem 0.7rem;
            border-radius: 14px;
            text-align: center;
        }

        .metric-label {
            font-size: 0.66rem;
            color: #94a3b8;
            letter-spacing: 0.15em;
            font-weight: 800;
            margin-bottom: 0.2rem;
        }

        .metric-value {
            font-size: 1.45rem;
            font-weight: 900;
            line-height: 1.1;
        }

        .dimension-list {
            display: grid;
            gap: 0.62rem;
            margin-top: 0.75rem;
        }

        .dimension-item {
            border: 1px solid rgba(165, 180, 252, 0.16);
            border-radius: 12px;
            background: rgba(148, 163, 184, 0.06);
            padding: 0.6rem 0.62rem 0.54rem;
        }

        .dimension-item.risk {
            border-color: rgba(251, 113, 133, 0.24);
            background: rgba(190, 24, 93, 0.08);
        }

        .dimension-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.55rem;
        }

        .dimension-label {
            font-size: 0.78rem;
            color: #e2e8f0;
            font-weight: 900;
            letter-spacing: 0.04em;
        }

        .dimension-score {
            font-size: 0.83rem;
            font-weight: 900;
            color: #c4b5fd;
            min-width: 2.1rem;
            text-align: right;
        }

        .dimension-item.risk .dimension-score {
            color: #fda4af;
        }

        .dimension-track {
            margin-top: 0.4rem;
            width: 100%;
            height: 0.34rem;
            border-radius: 9999px;
            background: rgba(148, 163, 184, 0.2);
            overflow: hidden;
        }

        .dimension-fill {
            height: 100%;
            border-radius: 9999px;
            background: var(--primary-gradient);
            box-shadow: var(--accent-glow);
            transition: width 0.45s ease;
        }

        .dimension-item.risk .dimension-fill {
            background: linear-gradient(90deg, #fb7185, #fda4af);
            box-shadow: 0 0 14px rgba(251, 113, 133, 0.4);
        }

        .dimension-basis {
            margin-top: 0.34rem;
            font-size: 0.69rem;
            color: #94a3b8;
            line-height: 1.45;
        }

        .unlock-area {
            display: flex;
            flex-direction: column;
            align-items: center;
            background: linear-gradient(to bottom, rgba(5,5,8,0) 0%, rgba(165,180,252,0.08) 100%);
            padding: 4rem 1.5rem;
            border-radius: 36px;
            border: 1px dashed rgba(165,180,252,0.25);
            margin-top: 1rem;
            position: relative;
            overflow: hidden;
        }

        .unlock-bg-text {
            position: absolute;
            inset: 10% 8%;
            white-space: pre-line;
            color: rgba(165, 180, 252, 0.22);
            font-size: 0.72rem;
            line-height: 1.8;
            filter: blur(2.6px);
            pointer-events: none;
            user-select: none;
            transform: rotate(-2deg);
            font-weight: 700;
            letter-spacing: 0.08em;
        }

        .mode-switch {
            display: inline-flex;
            align-items: center;
            border: 1px solid rgba(165, 180, 252, 0.35);
            border-radius: 9999px;
            padding: 2px;
            background: rgba(148, 163, 184, 0.08);
        }

        .mode-btn {
            border: none;
            background: transparent;
            color: #94a3b8;
            font-size: 0.7rem;
            font-weight: 900;
            letter-spacing: 0.2em;
            text-transform: uppercase;
            padding: 0.25rem 0.7rem;
            border-radius: 9999px;
            cursor: pointer;
            transition: all 0.25s;
        }

        .mode-btn.active {
            background: var(--primary-gradient);
            color: #050508;
            box-shadow: var(--accent-glow);
        }

        .hidden-report { display: none; }

        .btn-subtle {
            border: 1px solid rgba(165, 180, 252, 0.4);
            color: #cbd5e1;
            background: rgba(148, 163, 184, 0.08);
            font-weight: 800;
            letter-spacing: 0.12em;
        }

        .activation-modal {
            position: fixed;
            inset: 0;
            z-index: 140;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(2, 6, 23, 0.85);
            backdrop-filter: blur(8px);
            padding: 1.2rem;
        }

        .activation-card {
            width: 100%;
            max-width: 460px;
            border-radius: 24px;
            border: 1px solid rgba(165, 180, 252, 0.26);
            background: rgba(15, 23, 42, 0.92);
            box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
            padding: 1.4rem;
        }

        .activation-input {
            width: 100%;
            border: 1px solid rgba(148, 163, 184, 0.34);
            border-radius: 14px;
            background: rgba(2, 6, 23, 0.8);
            color: #f8fafc;
            padding: 0.85rem 0.95rem;
            font-size: 0.98rem;
            outline: none;
        }

        .activation-input:focus {
            border-color: #a5b4fc;
            box-shadow: 0 0 0 3px rgba(165, 180, 252, 0.22);
        }

        .case-shell {
            width: 100%;
            max-width: 860px;
            margin: 0 auto;
        }

        @media (max-width: 640px) {
            .report-card {
                padding: 1.2rem 0.85rem;
                border-radius: 20px;
                margin-bottom: 0.95rem;
            }
            .section-header {
                margin-bottom: 0.9rem;
            }
            .section-header h2 {
                font-size: 1.22rem;
            }
        }
    </style>
</head>
<body class="antialiased">

    <div class="max-w-xl mx-auto px-5 py-3 min-h-screen flex flex-col">

        <div id="screen-start" class="flex flex-col items-center text-center fade-in py-2 min-h-[calc(100vh-1.5rem)] justify-center gap-1">
            <p class="text-[0.56rem] tracking-[0.45em] text-indigo-400 mb-1 font-black uppercase">Qi Men Dun Jia · Destiny</p>
            <h1 class="text-[1.9rem] font-black mb-2 tracking-tight gradient-text">灵魂伴侣·正缘画像</h1>
            <div class="space-y-1 text-[1.03rem] leading-tight mb-2 font-medium text-slate-300">
                <p><strong>茫茫人海，谁是归处？</strong></p>
                <p class="gradient-text"><strong>不知良人何在，</strong><br><strong>莫测相逢何时。</strong></p>
                <p><strong>天盘九宫暗转，地盘八门藏机。</strong></p>
                <p><strong>若缘分将至，终会在命局指引中重逢。</strong></p>
            </div>
            <p class="text-[0.74rem] text-slate-300 mb-2 leading-snug max-w-[20rem]">
                如果你总在感情里反复内耗、总是遇到不确定的人，点击开始后系统会结合奇门盘为你锁定灵魂伴侣的<b>形象、性格、方位与相处策略</b>，帮你少走弯路，尽快遇见能稳定走下去的人。
            </p>
            <button onclick="showScreen('screen-gender')" class="btn-main px-10 py-2.5 rounded-2xl text-[0.95rem] tracking-[0.18em] mb-1.5">
                寻找正缘
            </button>
            <button onclick="showScreen('screen-case')" class="btn-subtle px-8 py-2 rounded-xl text-xs">
                查看正缘报告案例
            </button>
        </div>

        <div id="screen-case" class="hidden fade-in pb-16">
            <div class="case-shell">
                <div class="text-center mb-10">
                    <div class="inline-block px-4 py-1 rounded-full border border-indigo-500/30 text-[0.65rem] text-indigo-300 mb-4 font-black tracking-widest uppercase">Case Preview</div>
                    <h2 class="text-3xl font-black tracking-[0.2em] gradient-text">灵魂伴侣·正缘报告</h2>
                </div>

                <div class="report-card">
                    <div class="section-header"><h2>核心结论</h2></div>
                    <div class="report-text">
                        <p class="sentence-line">这段关系不是短期上头型，而是<b>先稳后深、越走越笃定</b>的长期关系。</p>
                        <p class="sentence-line">你会遇到的对象，核心优势是稳定、担当、执行力强，前期看起来慢热，但一旦确认关系，就会给出很实在的投入和托底。</p>
                        <p class="sentence-line">你们的关键课题不是“会不会遇到”，而是“你能不能从情绪反应切换到行动沟通”。只要这一步完成，关系会从反复试探进入长期并肩。</p>
                    </div>
                </div>

                <div class="report-card">
                    <div class="section-header"><h2>盘面八维</h2></div>
                    <div class="section-subtitle">纯奇门量化维度 + 原报告结构保留</div>
                    <div class="metric-grid">
                        <div class="metric-item">
                            <div class="metric-label">局势总分</div>
                            <div class="metric-value text-indigo-400">89</div>
                        </div>
                        <div class="metric-item">
                            <div class="metric-label">推进评级</div>
                            <div class="metric-value text-pink-400">A+</div>
                        </div>
                        <div class="metric-item">
                            <div class="metric-label">因缘契合度</div>
                            <div class="metric-value text-indigo-300">87%</div>
                        </div>
                        <div class="metric-item">
                            <div class="metric-label">能量稳定性</div>
                            <div class="metric-value text-pink-300">A</div>
                        </div>
                    </div>
                    <div class="dimension-list">
                        <div class="dimension-item">
                            <div class="dimension-row"><span class="dimension-label">沟通节奏</span><span class="dimension-score">84</span></div>
                            <div class="dimension-track"><div class="dimension-fill" style="width:84%"></div></div>
                            <p class="dimension-basis">前期节奏偏慢，但互动质量高，适合稳步推进（依据：开门、六合、天心）。</p>
                        </div>
                        <div class="dimension-item">
                            <div class="dimension-row"><span class="dimension-label">价值观</span><span class="dimension-score">82</span></div>
                            <div class="dimension-track"><div class="dimension-fill" style="width:82%"></div></div>
                            <p class="dimension-basis">对责任、秩序和长期投入的认知较一致（依据：天任星、值符、生门）。</p>
                        </div>
                        <div class="dimension-item">
                            <div class="dimension-row"><span class="dimension-label">稳定度</span><span class="dimension-score">86</span></div>
                            <div class="dimension-track"><div class="dimension-fill" style="width:86%"></div></div>
                            <p class="dimension-basis">关系托底能力较强，波动后可回稳（依据：生门、九地、天任星）。</p>
                        </div>
                        <div class="dimension-item">
                            <div class="dimension-row"><span class="dimension-label">行动力</span><span class="dimension-score">79</span></div>
                            <div class="dimension-track"><div class="dimension-fill" style="width:79%"></div></div>
                            <p class="dimension-basis">对方更偏“先观察后行动”，确认后执行力强（依据：开门、值使、马星）。</p>
                        </div>
                        <div class="dimension-item">
                            <div class="dimension-row"><span class="dimension-label">信任感</span><span class="dimension-score">81</span></div>
                            <div class="dimension-track"><div class="dimension-fill" style="width:81%"></div></div>
                            <p class="dimension-basis">需要通过真实沟通建立，不宜靠试探拉扯（依据：休门、太阴、天心）。</p>
                        </div>
                        <div class="dimension-item">
                            <div class="dimension-row"><span class="dimension-label">应期判断</span><span class="dimension-score">77</span></div>
                            <div class="dimension-track"><div class="dimension-fill" style="width:77%"></div></div>
                            <p class="dimension-basis">百日窗口内有两次明显推进点，宜主动把握（依据：值使、马星、开门）。</p>
                        </div>
                        <div class="dimension-item risk">
                            <div class="dimension-row"><span class="dimension-label">推进阻力</span><span class="dimension-score">43</span></div>
                            <div class="dimension-track"><div class="dimension-fill" style="width:43%"></div></div>
                            <p class="dimension-basis">阻力主要来自冷处理与误解，不是无缘分（依据：杜门、空亡、玄武）。</p>
                        </div>
                        <div class="dimension-item">
                            <div class="dimension-row"><span class="dimension-label">落地度</span><span class="dimension-score">83</span></div>
                            <div class="dimension-track"><div class="dimension-fill" style="width:83%"></div></div>
                            <p class="dimension-basis">从认识到稳定关系的现实承接度较高（依据：生门、值符、天辅）。</p>
                        </div>
                    </div>
                </div>

                <div class="report-card">
                    <div class="section-header"><h2>一、你的情况</h2></div>
                    <div class="report-text">
                        <p class="sentence-line">在推演对方之前，我们要先看清你在这张奇门盘里的定位。根据天干五合的法则，你是<b>【乙木】</b>。你在这段关系中展现出的，是典型的“外柔内刚、以柔克刚”的姿态。</p>
                        <h4 class="sub-heading">你的性格画像</h4>
                        <p class="sentence-line">你是一个典型的心思细腻者，甚至有点“想太多”。由于乙木的属性，你像藤蔓一样，虽然看起来温婉、好说话，但骨子里极具韧性。</p>
                        <p class="sentence-line">你不是那种硬碰硬的性格，你擅长曲线救国。你的脾气属于“冷处理型”——生气了不一定大吵大闹，但一定会变得极度沉默，让对方摸不着头脑。</p>
                        <h4 class="sub-heading">你的情感软肋</h4>
                        <p class="sentence-line">你容易被“秩序感”和“力量感”吸引。虽然你嘴上说想要自由，但潜意识里你一直在寻找一个能替你挡住风雨、甚至能“管得住”你的人。</p>
                        <p class="sentence-line">你有点小性子，但在大是大非面前，你比谁都清醒，是个典型的“利弊权衡高手”。</p>
                        <h4 class="sub-heading">为什么你会被他吸引</h4>
                        <p class="sentence-line">因为你底子里缺乏一种“确定的安全感”，而他那种像山一样的稳重和雄厚的物质基石，正好精准地填补了你内心的空缺。</p>
                        <p class="sentence-line">你和他在一起，不是简单的谈恋爱，而是一种<b>“生命秩序的重组”</b>。</p>
                    </div>
                </div>

                <div class="report-card">
                    <div class="section-header"><h2>二、正缘画像</h2></div>
                    <div class="report-text">
                        <p class="sentence-line">在你的奇门盘中，代表你未来另一半的能量集中在<b>【艮八宫】</b>。这个位置在地理上代表东北，在性质上代表“山”。为了让你看清这个人的全貌，我们需要把宫位里的四个核心符号——<b>神盘“值符”、星盘“天任星”、门盘“生门”以及干支格局“庚加戊”</b>揉碎了看。</p>
                        <p class="sentence-line">这组符号组合在一起，勾勒出了一个“极具实力的稳健型派系”男人的轮廓。值符代表他有社会地位，不是普通打工人；天任星说明他这人性格稳、耐操，甚至有点倔；生门直接点出了他的财富属性，他是带着“财气”来的；而庚加戊这种格局，说明他过去在事业上经历过大风大浪，现在的稳定是他一刀一枪拼出来的“硬资产”。</p>
                        <p class="sentence-line">他不是那种会玩花言巧语的“小鲜肉”，而是一个能够在你生活遇到危机时，直接掏出银行卡或者动用社会关系帮你平事的<b>“实力派大哥”</b>。</p>
                    </div>
                </div>

                <div class="report-card">
                    <div class="section-header"><h2>三、性格三观</h2></div>
                    <div class="report-text">
                        <p class="sentence-line">通过值符、天任与生门的交叉影响，这个人的性格主轴是务实、稳重、执行力强。</p>
                        <p class="sentence-line">他是一个极度务实的人。他不相信所谓的“纯爱”，他更相信资源置换和长久的情绪托底。受天任星的影响，他做事非常专注，甚至到了强迫症的程度。他在工作的时候不喜欢被打扰，但只要忙完了，他会把所有时间都留给你。</p>
                        <p class="sentence-line">值符赋予了他一种天然的掌控欲。在你们的相处中，他更倾向于做决定。比如去哪里吃饭、买什么样的房子、甚至是你的职业规划，他都会给出极其专业且无法反驳的建议。</p>
                        <p class="sentence-line">这种性格虽然显得有些霸道，但对于经常陷入选择困难症的你来说，却是一种极大的解脱。他是一个典型的“结果导向型”人格，他认为爱一个人的最高表现，就是让对方过上不用为钱操心的生活。</p>
                    </div>
                </div>

                <div class="report-card">
                    <div class="section-header"><h2>四、事业发展</h2></div>
                    <div class="report-text">
                        <p class="sentence-line">我们要看的职业画像，是基于生门和庚加戊的综合推演。</p>
                        <p class="sentence-line">这个男人目前大概率处于<b>“事业成熟期”</b>。他从事的行业多半与实业、建筑、大型金融运作或者是高壁垒的技术管理有关。他不是那种靠嘴皮子吃饭的，他的财富来自于对他所处行业资源的垄断或深耕。</p>
                        <p class="sentence-line">具体来说，他可能拥有自己的公司，或者是一家中大型企业的核心决策层。他名下的资产不是虚拟币或股票这种虚的东西，更多是看得见摸得着的房产、地皮或者是稳定的项目股权。</p>
                        <p class="sentence-line">由于庚金的影响，他在生意场上非常冷酷，对手都很怕他。但生门又保证了他的财源是源源不断的。他目前的财富量级，足以支撑你们在所在城市过上非常体面的、甚至是有阶层优越感的生活。</p>
                    </div>
                </div>

                <div class="report-card">
                    <div class="section-header"><h2>五、相貌外表</h2></div>
                    <div class="report-text">
                        <p class="sentence-line">这个男人的长相很有辨识度。他不是那种精致的帅，而是一种带着“厚重感”的气质。</p>
                        <p class="sentence-line">他的身高通常在178公分到185公分之间，骨架很大，肩膀很宽，是那种能撑起西装或挺括夹克的体型。他的脸部线条比较硬，尤其是下颌线和鼻梁，给人一种很有力量的感觉。他的发型永远是一丝不苟的，通常是干练的短发。</p>
                        <p class="sentence-line">他的眼神很深，平时不爱笑，看起来甚至有点凶。但当他看向你的时候，那种眼神里的专注度会让你觉得非常有安全感。</p>
                        <p class="sentence-line">他穿衣服非常讲究质感，他可能只有几件衣服，但每一件的剪裁和面料都是顶级的。他不喜欢大Logo，他更喜欢那种低调的、只有内行人才懂的奢侈。</p>
                        <p class="sentence-line">如果你在某个高端商务场合，看到一个坐姿笔挺、很少说话但气场最强、手腕上戴着一块低调名表的男人，那大概率就是他。</p>
                    </div>
                </div>

                <div class="report-card">
                    <div class="section-header"><h2>六、寻缘方向</h2></div>
                    <div class="report-text">
                        <p class="sentence-line">不要再去那些廉价的社交场所浪费时间了。他的活动轨迹非常有规律，通常只出现在能产生价值的地方。</p>
                        <p class="sentence-line">你要重点关注你所在城市的东北方位。具体场景包括：高规格的行业交流会、高端理财中心、私人高尔夫俱乐部、或者是那种入会门槛极高的茶室和书院。他也可能出现在一些正在开发的高端楼盘或者是大型项目的奠基仪式上。</p>
                        <p class="sentence-line">他不会主动在社交软件上打招呼。你们的相遇，大概率是通过一个非常有威望的男性长辈或事业导师介绍的。</p>
                        <p class="sentence-line">这个中间人说话很有分量，他会以“介绍生意”或者“认识一下”为名，把你们拉到一个局里。所以，当有这种看起来比较正式的饭局邀请时，千万不要拒绝，因为他就在那里等着你。</p>
                    </div>
                </div>

                <div class="report-card">
                    <div class="section-header"><h2>七、相处状态</h2></div>
                    <div class="report-text">
                        <p class="sentence-line">当你的“乙木”遇到他的“庚金”，这不仅是情感的结合，更是一场<b>“修剪与攀附”</b>的博弈。</p>
                        <h4 class="sub-heading">相处的感觉：起初是“崇拜”，后期是“磨合”</h4>
                        <p class="sentence-line">刚接触时，你会觉得他太有安全感了，那种万事皆有定数、出门不用带脑子的感觉让你很上头。但时间一长，受他“值符”和“庚金”的影响，你会觉得他控制欲太强，像个严厉的教导主任。他会管你的开销、管你的社交圈，甚至管你的作息。</p>
                        <h4 class="sub-heading">沟通的雷区：千万别跟他“冷战”</h4>
                        <p class="sentence-line">他是一个“天任星”入宫的人，轴得很。如果你想通过冷战让他低头，那你输定了，他能憋到地老天荒。对付他这种“硬骨头”，你得发挥你乙木的优势——“绕着走”。当他硬邦邦地提要求时，你别正面刚，撒个娇或者换个话题，等他脾气过了，他会因为刚才的霸道而通过金钱或物质补偿你。</p>
                        <h4 class="sub-heading">长久发展的核心：成为他的“首席参谋”</h4>
                        <p class="sentence-line">他虽然在外面叱咤风云，但内心其实很孤独。他需要的不仅仅是一个漂亮老婆，而是一个能听懂他生意经、能在他疲惫时给出一个准确建议的伴侣。你们的相处模式最好是：他负责攻城略地，你负责后勤外交。</p>
                    </div>
                </div>

                <div class="report-card">
                    <div class="section-header"><h2>八、相处策略</h2></div>
                    <div class="report-text">
                        <p class="sentence-line">搞定这种男人，靠撒娇卖萌是不够的，你需要一套“降维打击”的策略。</p>
                        <p class="sentence-line">首先，你要表现出你的<b>“智性美”</b>。这种男人见过太多漂亮皮囊，他更在意的是你能不能听懂他的逻辑。当他在聊事业、聊趋势的时候，你不需要比他更懂，但你一定要能给到正向的情绪反馈。</p>
                        <p class="sentence-line">其次，学会适度<b>“示弱”</b>。虽然他喜欢强大的伴侣，但他骨子里的值符基因渴望保护弱者。当你表现出你在某些决策上的纠结时，要把球踢给他，让他来展现力量。</p>
                        <p class="sentence-line">最后，在视觉上要向他的审美靠拢。他喜欢那种<b>“贵气且内敛”</b>的女性形象。多穿米色、香槟色、质感好的丝绸或羊绒面料。不要穿得太浮夸或太廉价。</p>
                        <p class="sentence-line">你要让自己看起来像是一个“见过世面、情绪稳定、且能和他一起守住家业”的女人。只要你表现出这种稳定性，他会像保护他的资产一样，终身守护你。</p>
                    </div>
                </div>

                <div class="report-card bg-indigo-900/10 border-indigo-400/30">
                    <div class="section-header"><h2>结语：百日窗口期，别掉链子</h2></div>
                    <div class="report-text">
                        <p class="sentence-line">从现在开始到接下来的三个月，是你姻缘能量最强的时候。</p>
                        <p class="sentence-line">这段时间，请你留意身边生肖属牛、属虎的人，或者是名字里带“土”或“金”偏旁的人。他们很可能是那个开启你新生活的关键人物。</p>
                        <p class="sentence-line">别再在那些没意义的感情里内耗了，腾出你的空间，那个如山一般稳重的男人，正在通过他的社交圈向你靠近。</p>
                        <p class="sentence-line">只要你准备好了，剩下的交给命运。</p>
                    </div>
                </div>

                <div class="text-center">
                    <button onclick="showScreen('screen-start')" class="btn-main px-10 py-4 rounded-2xl text-base tracking-[0.15em]">
                        返回首页
                    </button>
                </div>
            </div>
        </div>

        <div id="screen-gender" class="hidden flex flex-col items-center text-center fade-in py-12">
            <h2 class="text-3xl font-black mb-16 tracking-widest text-slate-100">请选择你的性别</h2>
            <div class="flex gap-8 w-full justify-center px-6">
                <button onclick="startCalculation('male')" class="flex-1 border-2 border-slate-800 rounded-[2.5rem] p-10 bg-white/5 hover:border-indigo-400 transition-all group">
                    <span class="text-8xl font-black mb-6 block group-hover:scale-110 transition-transform">男</span>
                    <span class="text-sm font-bold text-slate-500 tracking-[0.5em]">GENTLEMAN</span>
                </button>
                <button onclick="startCalculation('female')" class="flex-1 border-2 border-slate-800 rounded-[2.5rem] p-10 bg-white/5 hover:border-pink-400 transition-all group">
                    <span class="text-8xl font-black mb-6 block group-hover:scale-110 transition-transform">女</span>
                    <span class="text-sm font-bold text-slate-500 tracking-[0.5em]">LADY</span>
                </button>
            </div>
        </div>

        <div id="screen-loading" class="hidden flex flex-col items-center text-center fade-in py-12">
            <div class="abstract-portrait mb-16">
                <div class="energy-core"></div>
            </div>
            <h2 class="text-3xl font-black gradient-text mb-8 tracking-widest animate-pulse">正在连通星盘能量</h2>
            <p id="loading-note" class="text-slate-500 mb-2 font-bold tracking-widest">正在起局中，请诚心思考你的灵魂伴侣……</p>
            <div class="w-64 h-1.5 bg-white/5 relative overflow-hidden mt-10 rounded-full">
                <div id="progress-bar" class="absolute left-0 top-0 h-full bg-indigo-400 transition-all duration-[1200ms] w-0 shadow-[0_0_15px_#818cf8]"></div>
            </div>
        </div>

        <div id="screen-result" class="hidden pb-24 fade-in">
            <div class="text-center mb-12">
                <div class="inline-block px-4 py-1 rounded-full border border-indigo-500/30 text-[0.65rem] text-indigo-400 mb-4 font-black tracking-widest uppercase">Confidential Report</div>
                <h2 class="text-3xl font-black tracking-[0.2em] gradient-text">灵魂伴侣正缘报告</h2>
                <p id="pan-meta" class="mt-4 text-xs text-slate-500 tracking-widest"></p>
                <div class="mt-4 mode-switch">
                    <button id="mode-direct-btn" type="button" class="mode-btn active" onclick="setExplainMode('direct')">直断</button>
                    <button id="mode-detail-btn" type="button" class="mode-btn" onclick="setExplainMode('detail')">详解</button>
                </div>
            </div>

            <div id="detail-pan-evidence" class="hidden report-card mb-8">
                <div class="section-header"><h2>详解模式 · 盘面符号依据</h2></div>
                <div id="detail-pan-evidence-content" class="report-text"></div>
            </div>

            <div id="preview-sections">
                <div class="report-card">
                    <div class="metric-grid">
                        <div class="metric-item">
                            <div class="metric-label">局势总分</div>
                            <div id="score-overall" class="metric-value text-indigo-400">88</div>
                        </div>
                        <div class="metric-item">
                            <div class="metric-label">推进评级</div>
                            <div id="score-grade" class="metric-value text-pink-400">A+</div>
                        </div>
                        <div class="metric-item">
                            <div class="metric-label">因缘契合度</div>
                            <div id="score-compatibility" class="metric-value text-indigo-300">86%</div>
                        </div>
                        <div class="metric-item">
                            <div class="metric-label">能量稳定性</div>
                            <div id="score-stability" class="metric-value text-pink-300">A</div>
                        </div>
                    </div>

                    <div class="sub-heading">盘面八维</div>
                    <div id="dimension-list" class="dimension-list"></div>

                    <div class="sub-heading">磁场深度解读</div>
                    <div id="hook-content" class="report-text text-slate-300 leading-relaxed text-sm md:text-base space-y-4">
                        <p>盘局已生成，正在解析你的灵魂伴侣磁场...</p>
                    </div>
                </div>

                <div class="unlock-area">
                    <div class="unlock-bg-text">山势沉稳，先慢后深。\n初遇慌乱，不是错的人，是节奏没对。\n你越想快，反而越难看清真心。\n把标准放在“稳定负责”，缘分会靠近。</div>
                    <div class="w-20 h-20 bg-indigo-500/15 rounded-full flex items-center justify-center mb-8 border border-indigo-400/30 shadow-2xl">
                        <svg class="w-10 h-10 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 00-2 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h3 class="text-2xl font-black mb-4">良人已定，请君入局</h3>
                    <p class="text-slate-400 text-center mb-10 px-8 leading-loose text-sm">
                        我们已通过奇门局为您锁定了对方的<br>
                        <b>性格画像、事业财富、相貌特征、相处秘籍</b><br>
                        以及精准的<b>百日缘分窗口期</b>。
                    </p>
                    <button onclick="handleUnlock()" class="btn-main px-16 py-6 rounded-[2rem] text-2xl tracking-widest w-full max-w-sm">
                        立即解锁全部内容
                    </button>
                    <p class="mt-8 text-[0.7rem] text-slate-600 font-bold tracking-[0.4em] uppercase">Private & Confidential</p>
                </div>
            </div>

            <div id="full-report" class="hidden-report space-y-8">
                <div id="full-report-content" class="space-y-8">
                    <div class="report-card">
                        <div class="section-header"><h2>报告生成中</h2></div>
                        <div class="report-text"><p>点击“立即解锁全部内容”后，将基于同一奇门局生成完整灵魂伴侣报告。</p></div>
                    </div>
                </div>
            </div>
        </div>

        <div id="loading-overlay" class="hidden fixed inset-0 bg-black/95 backdrop-blur-3xl z-[100] flex flex-col items-center justify-center">
            <div class="energy-core scale-150 mb-12"></div>
            <p id="unlock-note" class="text-indigo-300 font-black tracking-[0.5em] text-xl animate-pulse">正在认真推算中，请诚心思考你的灵魂伴侣……</p>
        </div>

        <div id="activation-modal" class="hidden activation-modal">
            <div class="activation-card">
                <h3 class="text-2xl font-black gradient-text mb-4">请输入激活码</h3>
                <p class="text-slate-400 text-sm leading-relaxed mb-4">输入激活码后即可解锁完整正缘报告。</p>
                <input id="activation-code-input" class="activation-input" type="text" placeholder="请输入激活码" />
                <p id="activation-error" class="text-rose-300 text-xs mt-3 hidden">请输入激活码后再继续。</p>
                <p class="text-slate-400 text-xs mt-3">请添加若轩老师微信领取激活码</p>
                <div class="mt-5 flex gap-3">
                    <button onclick="closeActivationModal()" class="flex-1 rounded-xl border border-slate-600 text-slate-200 py-2.5 font-bold">取消</button>
                    <button onclick="confirmActivationAndUnlock()" class="flex-1 btn-main rounded-xl py-2.5 text-base">确认解锁</button>
                </div>
            </div>
        </div>
    </div>

    <script>
        var CHANNEL = '${CHANNEL}';
        var currentGender = '';
        var startPending = false;
        var unlockPending = false;
        var currentExplainMode = 'direct';
        var hookRawText = '';
        var fullRawText = '';
        var panSummaryRaw = '';
        var activationCode = '';
        var dimensionRowsRaw = [];

        function showScreen(screenId) {
            document.querySelectorAll('div[id^="screen-"]').forEach(function(el) { el.classList.add('hidden'); });
            var target = document.getElementById(screenId);
            if (!target) return;
            target.classList.remove('hidden');
            target.classList.add('fade-in');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        function notifyHost(type, payload) {
            try {
              window.parent.postMessage({ channel: CHANNEL, type: type, payload: payload || {} }, '*');
            } catch (_) {}
        }

        function setLoadingProgress(widthPercent) {
            var bar = document.getElementById('progress-bar');
            if (!bar) return;
            bar.style.width = String(Math.max(0, Math.min(100, Number(widthPercent) || 0))) + '%';
        }

        function stripBracketExplanationsForDirect(input) {
            var text = String(input || '');
            var prev = '';
            while (text !== prev) {
                prev = text;
                text = text.replace(/（[^（）]*）/g, '').replace(/\([^()]*\)/g, '');
            }
            return text
                .replace(/[ \t]{2,}/g, ' ')
                .replace(/\n{3,}/g, '\n\n')
                .replace(/\s+([，。！？；：])/g, '$1')
                .trim();
        }

        function applyExplainMode(rawText) {
            var source = String(rawText || '');
            if (!source) return '';
            if (currentExplainMode === 'detail') return source;
            return stripBracketExplanationsForDirect(source);
        }

        function setExplainMode(mode) {
            currentExplainMode = mode === 'detail' ? 'detail' : 'direct';
            var directBtn = document.getElementById('mode-direct-btn');
            var detailBtn = document.getElementById('mode-detail-btn');
            if (directBtn) directBtn.classList.toggle('active', currentExplainMode === 'direct');
            if (detailBtn) detailBtn.classList.toggle('active', currentExplainMode === 'detail');
            renderDetailPanEvidence();
            renderDimensionRowsByMode();
            renderHookByMode();
            renderFullReportByMode();
        }

        function escapeHtml(raw) {
            return String(raw || '')
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;');
        }

        function applyInlineMarkdown(text) {
            return String(text || '').replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
        }

        function splitBySentence(text) {
            var source = String(text || '').trim();
            if (!source) return [];
            var normalized = source.replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ').trim();
            var parts = normalized.split(/(?<=[。！？；])/u).map(function(item) { return item.trim(); }).filter(Boolean);
            if (!parts.length) return [normalized];
            return parts;
        }

        function parsePanSummaryEvidence(summaryText) {
            var source = String(summaryText || '').trim();
            if (!source) return { meta: [], palaces: [] };
            var lines = source.split('\n').map(function(line) { return String(line || '').trim(); }).filter(Boolean);
            var meta = [];
            var palaces = [];
            lines.forEach(function(line) {
                if (/^\[\d+宫/.test(line)) {
                    var m = line.match(/^\[(\d+宫\s+[^\]]+)\]\s*神:([^\s]+)\s*门:([^\s]+)\s*星:([^\s]+)\s*天盘:([^\s]+)\s*地盘:([^\s]+)$/);
                    if (m) {
                        palaces.push({
                            palace: m[1],
                            deity: m[2],
                            gate: m[3],
                            star: m[4],
                            heaven: m[5],
                            earth: m[6],
                        });
                    } else {
                        palaces.push({ raw: line });
                    }
                    return;
                }
                if (!/^【/.test(line) && line.indexOf('九宫摘要') < 0 && meta.length < 8) {
                    meta.push(line);
                }
            });
            return { meta: meta, palaces: palaces };
        }

        function renderDetailPanEvidence() {
            var wrap = document.getElementById('detail-pan-evidence');
            var content = document.getElementById('detail-pan-evidence-content');
            if (!wrap || !content) return;
            if (currentExplainMode !== 'detail' || !panSummaryRaw) {
                wrap.classList.add('hidden');
                content.innerHTML = '';
                return;
            }
            var parsed = parsePanSummaryEvidence(panSummaryRaw);
            var html = [];
            if (parsed.meta && parsed.meta.length) {
                html.push('<h4 class="sub-heading">本局关键信息</h4>');
                parsed.meta.forEach(function(line) {
                    html.push('<p class="sentence-line">' + escapeHtml(line) + '</p>');
                });
            }
            if (parsed.palaces && parsed.palaces.length) {
                html.push('<h4 class="sub-heading">九宫符号速览（详解专用）</h4>');
                parsed.palaces.forEach(function(item) {
                    if (item.raw) {
                        html.push('<p class="sentence-line">' + escapeHtml(item.raw) + '</p>');
                        return;
                    }
                    html.push('<p class="sentence-line"><b>' + escapeHtml(item.palace) + '</b>：神 ' + escapeHtml(item.deity) + '，门 ' + escapeHtml(item.gate) + '，星 ' + escapeHtml(item.star) + '，天盘 ' + escapeHtml(item.heaven) + '，地盘 ' + escapeHtml(item.earth) + '</p>');
                });
            }
            content.innerHTML = html.join('');
            wrap.classList.remove('hidden');
        }

        function renderHookText(text) {
            var wrap = document.getElementById('hook-content');
            if (!wrap) return;
            var chunks = String(text || '').split(/\n{2,}/).map(function(item) { return item.trim(); }).filter(Boolean);
            if (!chunks.length) {
                wrap.innerHTML = '<p>盘局已生成，等待内容返回...</p>';
                return;
            }
            wrap.innerHTML = chunks.map(function(chunk) {
                var sentences = splitBySentence(chunk);
                return sentences.map(function(sentence) {
                    return '<p class="sentence-line">' + applyInlineMarkdown(escapeHtml(sentence)) + '</p>';
                }).join('');
            }).join('');
        }

        function renderHookByMode() {
            renderHookText(applyExplainMode(hookRawText));
        }

        function normalizeDimensionRows(rawRows) {
            if (!Array.isArray(rawRows)) return [];
            return rawRows
              .map(function(item) {
                  if (!item || typeof item !== 'object') return null;
                  var label = String(item.label || '').trim();
                  if (!label) return null;
                  var rawScore = Number(item.score || 0);
                  var score = Math.max(0, Math.min(100, Number.isFinite(rawScore) ? Math.round(rawScore) : 0));
                  var direction = String(item.direction || '').trim() === 'risk' ? 'risk' : 'positive';
                  var basis = String(item.basis || '').trim();
                  return { label: label, score: score, direction: direction, basis: basis };
              })
              .filter(Boolean);
        }

        function renderDimensionRowsByMode() {
            var wrap = document.getElementById('dimension-list');
            if (!wrap) return;
            var rows = normalizeDimensionRows(dimensionRowsRaw);
            if (!rows.length) {
                wrap.innerHTML = '<p class="dimension-basis">维度结果暂未就绪。</p>';
                return;
            }
            wrap.innerHTML = rows.map(function(row) {
                var safeLabel = escapeHtml(row.label);
                var safeScore = String(row.score);
                var basisText = row.basis ? applyExplainMode(row.basis) : '';
                var safeBasis = basisText ? '<p class="dimension-basis">' + escapeHtml(basisText) + '</p>' : '';
                var itemClass = row.direction === 'risk' ? 'dimension-item risk' : 'dimension-item';
                return [
                    '<div class="' + itemClass + '">',
                    '<div class="dimension-row">',
                    '<span class="dimension-label">' + safeLabel + '</span>',
                    '<span class="dimension-score">' + safeScore + '</span>',
                    '</div>',
                    '<div class="dimension-track"><div class="dimension-fill" style="width:' + safeScore + '%"></div></div>',
                    safeBasis,
                    '</div>',
                ].join('');
            }).join('');
        }

        function parseReportSections(raw) {
            var text = String(raw || '').replace(/\r/g, '').trim();
            if (!text) return [];
            var lines = text.split('\n');
            var sections = [];
            var current = null;

            function pushCurrent() {
                if (current && current.lines.length) sections.push(current);
            }

            lines.forEach(function(line) {
                var trimmed = String(line || '').trim();
                if (/^#\s+/.test(trimmed)) return;
                var h2Match = trimmed.match(/^##\s+(.+)$/);
                if (h2Match) {
                    pushCurrent();
                    current = { title: h2Match[1].trim(), lines: [] };
                    return;
                }
                if (!current) current = { title: '灵魂伴侣报告', lines: [] };
                current.lines.push(line || '');
            });
            pushCurrent();
            return sections;
        }

        function getSectionSubtitle(title, index) {
            var text = String(title || '').trim();
            if (!text) return '';
            if (text.indexOf('核心结论') >= 0) return '先看结论，再看推进节奏';
            if (text.indexOf('盘面八维') >= 0) return '纯奇门量化维度';
            if (text.indexOf('一、') >= 0 || text.indexOf('你的情况') >= 0) return '你的情感模式与真实需求';
            if (text.indexOf('二、') >= 0 || text.indexOf('正缘画像') >= 0) return '对方轮廓、气场与身份感';
            if (text.indexOf('三、') >= 0 || text.indexOf('性格三观') >= 0) return '价值观与相处逻辑';
            if (text.indexOf('四、') >= 0 || text.indexOf('事业发展') >= 0) return '行业、资产与职业阶段';
            if (text.indexOf('五、') >= 0 || text.indexOf('相貌外表') >= 0) return '长相身材与穿着偏好';
            if (text.indexOf('六、') >= 0 || text.indexOf('寻缘方向') >= 0) return '方位与高概率场景';
            if (text.indexOf('七、') >= 0 || text.indexOf('相处状态') >= 0) return '磨合雷区与长期模式';
            if (text.indexOf('八、') >= 0 || text.indexOf('相处策略') >= 0) return '可执行关系推进方案';
            if (text.indexOf('结语') >= 0) return '最后一句提醒';
            return '';
        }

        function countSectionSubHeadings(lines) {
            if (!Array.isArray(lines) || !lines.length) return 0;
            return lines.reduce(function(count, line) {
                var trimmed = String(line || '').trim();
                if (/^###\s+/.test(trimmed)) return count + 1;
                return count;
            }, 0);
        }

        function renderSectionContent(lines, sectionTitle, keepSubHeadings) {
            var html = [];
            var coreSection = String(sectionTitle || '').indexOf('核心结论') >= 0;
            var canShowSubHeading = keepSubHeadings === true;

            lines.forEach(function(line) {
                var raw = String(line || '');
                var trimmed = raw.trim();
                if (coreSection) {
                    trimmed = trimmed.replace(/^[-•*]\s+/, '').replace(/^\d+[.、]\s+/, '').trim();
                }
                if (!trimmed) {
                    return;
                }

                var h3Match = trimmed.match(/^###\s+(.+)$/);
                if (h3Match) {
                    if (canShowSubHeading) {
                        html.push('<h4 class="sub-heading">' + escapeHtml(h3Match[1].trim()) + '</h4>');
                    }
                    return;
                }

                if (!coreSection) {
                    trimmed = trimmed.replace(/^[-•*]\s+/, '').replace(/^\d+[.、]\s+/, '').trim();
                }
                var sentences = splitBySentence(trimmed);
                if (!sentences.length) return;
                sentences.forEach(function(sentence) {
                    html.push('<p class="sentence-line">' + applyInlineMarkdown(escapeHtml(sentence)) + '</p>');
                });
            });
            return html.join('');
        }

        function renderFullReport(text) {
            var wrap = document.getElementById('full-report-content');
            if (!wrap) return;
            var sections = parseReportSections(text);
            if (!sections.length) {
                var fallbackSentences = splitBySentence(text || '暂无内容');
                wrap.innerHTML = '<div class="report-card"><div class="section-header"><h2>灵魂伴侣报告</h2></div><div class="report-text">' + fallbackSentences.map(function(sentence) { return '<p class="sentence-line">' + applyInlineMarkdown(escapeHtml(sentence)) + '</p>'; }).join('') + '</div></div>';
                return;
            }
            wrap.innerHTML = sections.map(function(section, index) {
                var title = String(section.title || '').trim() || '灵魂伴侣报告';
                var subtitle = getSectionSubtitle(title, index);
                var subHeadingCount = countSectionSubHeadings(section.lines || []);
                var keepSubHeadings = subHeadingCount > 2;
                return [
                    '<div class="report-card">',
                    '<div class="section-header"><h2>' + escapeHtml(title) + '</h2></div>',
                    (subtitle ? '<div class="section-subtitle">' + escapeHtml(subtitle) + '</div>' : ''),
                    '<div class="report-text">' + renderSectionContent(section.lines || [], title, keepSubHeadings) + '</div>',
                    '</div>'
                ].join('');
            }).join('');
        }

        function renderFullReportByMode() {
            if (!fullRawText) return;
            renderFullReport(applyExplainMode(fullRawText));
        }

        function startCalculation(gender) {
            if (startPending) return;
            startPending = true;
            currentGender = String(gender || '').trim();
            var loadingNote = document.getElementById('loading-note');
            if (loadingNote) loadingNote.textContent = '正在起局中，请诚心思考你的灵魂伴侣……';
            showScreen('screen-loading');
            setLoadingProgress(8);
            setTimeout(function() { setLoadingProgress(42); }, 120);
            notifyHost('start-generate', { gender: currentGender });
        }

        function handleUnlock() {
            if (unlockPending) return;
            openActivationModal();
        }

        function openActivationModal() {
            var modal = document.getElementById('activation-modal');
            var input = document.getElementById('activation-code-input');
            var err = document.getElementById('activation-error');
            if (err) err.classList.add('hidden');
            if (modal) modal.classList.remove('hidden');
            setTimeout(function() {
                if (input && typeof input.focus === 'function') input.focus();
            }, 80);
        }

        function closeActivationModal() {
            var modal = document.getElementById('activation-modal');
            var err = document.getElementById('activation-error');
            if (err) err.classList.add('hidden');
            if (modal) modal.classList.add('hidden');
        }

        function confirmActivationAndUnlock() {
            if (unlockPending) return;
            var input = document.getElementById('activation-code-input');
            var err = document.getElementById('activation-error');
            var code = input ? String(input.value || '').trim() : '';
            if (!code) {
                if (err) err.classList.remove('hidden');
                return;
            }
            activationCode = code;
            closeActivationModal();
            unlockPending = true;
            var overlay = document.getElementById('loading-overlay');
            var unlockNote = document.getElementById('unlock-note');
            if (unlockNote) unlockNote.textContent = '正在认真推算中，请诚心思考你的灵魂伴侣……';
            if (overlay) overlay.classList.remove('hidden');
            notifyHost('unlock-generate', { gender: currentGender, activationCode: activationCode });
        }

        window.addEventListener('message', function(event) {
            var data = event && event.data;
            if (!data || data.channel !== CHANNEL) return;
            var type = String(data.type || '').trim();
            var payload = data.payload || {};

            if (type === 'start-success') {
                startPending = false;
                setLoadingProgress(100);
                hookRawText = String(payload.hookText || '');
                panSummaryRaw = String(payload.panSummary || '');
                dimensionRowsRaw = Array.isArray(payload.dimensions) ? payload.dimensions : [];
                renderDetailPanEvidence();
                renderDimensionRowsByMode();
                renderHookByMode();
                var scoreOverall = document.getElementById('score-overall');
                var scoreGrade = document.getElementById('score-grade');
                var scoreCompatibility = document.getElementById('score-compatibility');
                var scoreStability = document.getElementById('score-stability');
                var panMeta = document.getElementById('pan-meta');
                if (scoreOverall) scoreOverall.textContent = String(payload.overallScore ?? '88');
                if (scoreGrade) scoreGrade.textContent = String(payload.gradeText || 'A+');
                if (scoreCompatibility) scoreCompatibility.textContent = String(payload.compatibilityPercent || '86%');
                if (scoreStability) scoreStability.textContent = String(payload.stabilityText || 'A');
                if (panMeta) {
                    var modeText = String(payload.modeText || '').trim();
                    var sourceTime = String(payload.sourceDateTime || '').trim();
                    panMeta.textContent = [modeText, sourceTime].filter(Boolean).join(' · ');
                }
                setTimeout(function() {
                    showScreen('screen-result');
                    setLoadingProgress(0);
                }, 260);
                return;
            }

            if (type === 'start-error') {
                startPending = false;
                setLoadingProgress(0);
                alert(String(payload.message || '生成失败，请重试'));
                showScreen('screen-gender');
                return;
            }

            if (type === 'unlock-success') {
                unlockPending = false;
                var overlay = document.getElementById('loading-overlay');
                if (overlay) overlay.classList.add('hidden');
                closeActivationModal();
                fullRawText = String(payload.fullReportText || '');
                renderFullReportByMode();
                var preview = document.getElementById('preview-sections');
                if (preview) preview.classList.add('hidden');
                var fullReport = document.getElementById('full-report');
                if (fullReport) {
                    fullReport.classList.remove('hidden-report');
                    fullReport.classList.add('fade-in');
                }
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            if (type === 'unlock-error') {
                unlockPending = false;
                var overlay = document.getElementById('loading-overlay');
                if (overlay) overlay.classList.add('hidden');
                alert(String(payload.message || '解锁失败，请重试'));
            }
        });

        var activationInput = document.getElementById('activation-code-input');
        if (activationInput) {
            activationInput.addEventListener('keydown', function(ev) {
                if (ev && ev.key === 'Enter') {
                    confirmActivationAndUnlock();
                }
            });
        }

        setExplainMode('direct');
        notifyHost('iframe-ready', {});
    </script>
</body>
</html>
`;

function randomIntInclusive(min: number, max: number): number {
  const low = Math.min(min, max);
  const high = Math.max(min, max);
  return Math.floor(Math.random() * (high - low + 1)) + low;
}

function buildRandomPanRequest(): PanCalculateOptions {
  const now = Date.now();
  const offsetDays = randomIntInclusive(-420, 420);
  const offsetMinutes = randomIntInclusive(0, 1439);
  const dt = new Date(now + offsetDays * 24 * 60 * 60 * 1000 + offsetMinutes * 60 * 1000);
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  const hour = String(dt.getHours()).padStart(2, '0');
  const minute = String(dt.getMinutes()).padStart(2, '0');
  const panMode: 'hour' | 'ke' = Math.random() < 0.5 ? 'hour' : 'ke';
  const manualJu = Math.random() < 0.45 ? randomIntInclusive(1, 9) : null;

  return {
    dateTime: `${year}-${month}-${day}T${hour}:${minute}`,
    panMode,
    question: '',
    keIndex: randomIntInclusive(0, 5),
    manualJu,
  };
}

function toBooleanFlag(value: unknown): boolean {
  if (value === true) return true;
  if (value === false || value == null) return false;
  const text = String(value).trim().toLowerCase();
  return text === '1' || text === 'true' || text === 'yes';
}

function normalizePanPalaces(pan: unknown): PanPalaceCell[] {
  const out: PanPalaceCell[] = [];
  const payload = (pan && typeof pan === 'object') ? pan as Record<string, unknown> : {};
  const raw = payload.palaces;

  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      if (!Array.isArray(item) || item.length < 2) return;
      const pos = Number(item[0]);
      const cell = item[1];
      if (!Number.isFinite(pos) || !cell || typeof cell !== 'object') return;
      const src = cell as Record<string, unknown>;
      out.push({
        position: pos,
        trigram: String(src.trigram || ''),
        deitySymbol: String(src.deitySymbol || ''),
        gateSymbol: String(src.gateSymbol || ''),
        starSymbol: String(src.starSymbol || ''),
        heavenlyStem: String(src.heavenlyStem || ''),
        earthlyStem: String(src.earthlyStem || ''),
        hasVoid: toBooleanFlag(src.hasVoid),
        hasTomb: toBooleanFlag(src.hasTomb),
        hasXing: toBooleanFlag(src.hasXing),
        hasMenPo: toBooleanFlag(src.hasMenPo),
        isHorse: toBooleanFlag(src.isHorse),
        isZhiFu: toBooleanFlag(src.isZhiFu),
        isZhiShi: toBooleanFlag(src.isZhiShi),
      });
    });
  } else if (raw && typeof raw === 'object') {
    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
      const pos = Number(key);
      if (!Number.isFinite(pos) || !value || typeof value !== 'object') return;
      const src = value as Record<string, unknown>;
      out.push({
        position: pos,
        trigram: String(src.trigram || ''),
        deitySymbol: String(src.deitySymbol || ''),
        gateSymbol: String(src.gateSymbol || ''),
        starSymbol: String(src.starSymbol || ''),
        heavenlyStem: String(src.heavenlyStem || ''),
        earthlyStem: String(src.earthlyStem || ''),
        hasVoid: toBooleanFlag(src.hasVoid),
        hasTomb: toBooleanFlag(src.hasTomb),
        hasXing: toBooleanFlag(src.hasXing),
        hasMenPo: toBooleanFlag(src.hasMenPo),
        isHorse: toBooleanFlag(src.isHorse),
        isZhiFu: toBooleanFlag(src.isZhiFu),
        isZhiShi: toBooleanFlag(src.isZhiShi),
      });
    });
  }

  out.sort((a, b) => a.position - b.position);
  return out;
}

function formatPanSummary(resp: PanCalculateResponse, req: PanCalculateOptions): string {
  const payload = (resp?.pan && typeof resp.pan === 'object') ? resp.pan as Record<string, unknown> : {};
  const mode = String(resp?.mode || req.panMode || 'hour').trim() === 'ke' ? '刻盘' : '时盘';
  const palaces = normalizePanPalaces(payload);

  const pillarLine = [
    `四柱：年${String(payload.yearPillar || '-')}`,
    `月${String(payload.monthPillar || '-')}`,
    `日${String(payload.dayPillar || '-')}`,
    `时${String(payload.hourPillar || '-')}`,
    (mode === '刻盘' ? `刻${String(payload.kePillar || '-')}` : ''),
  ].filter(Boolean).join(' ');

  const lines: string[] = [
    '【灵魂伴侣随机测试局】',
    `类型：${mode} · ${String(payload.juText || '-')}`,
    '事项：无（随机起局，不带问题）',
    `排盘时间：${String(resp?.sourceDateTime || req.dateTime || '').replace('T', ' ')}`,
    `节气：${String(payload.solarTermText || '-')}`,
    pillarLine,
    `旬首：${String(payload.xunShou || '-')}；值符：${String(payload.zhiFu || '-')}；值使：${String(payload.zhiShi || '-')}`,
    `空亡：${String(payload.kongText || '-')}；马星：${String(payload.maText || '-')}`,
    '',
    '【九宫摘要】',
  ];

  if (!palaces.length) {
    lines.push('（未获取到宫位详情）');
  } else {
    palaces.forEach((cell) => {
      lines.push(
        [
          `[${cell.position}宫 ${String(cell.trigram || '-')}]`,
          `神:${String(cell.deitySymbol || '-')}`,
          `门:${String(cell.gateSymbol || '-')}`,
          `星:${String(cell.starSymbol || '-')}`,
          `天盘:${String(cell.heavenlyStem || '-')}`,
          `地盘:${String(cell.earthlyStem || '-')}`,
        ].join(' '),
      );
    });
  }

  return lines.join('\n');
}

function resolveGenderLabel(gender: string): string {
  const key = String(gender || '').trim().toLowerCase();
  if (key === 'male' || key === '男') return '男';
  if (key === 'female' || key === '女') return '女';
  return '未说明';
}

type SymbolWeightMap = Record<string, number>;

interface QimenDimensionRule {
  key: string;
  label: string;
  direction: 'positive' | 'risk';
  base: number;
  scale: number;
  focus: string;
  gateWeights: SymbolWeightMap;
  starWeights: SymbolWeightMap;
  deityWeights: SymbolWeightMap;
  flagWeights: {
    void: number;
    tomb: number;
    xing: number;
    menPo: number;
    horse: number;
    zhiFu: number;
    zhiShi: number;
  };
}

const QIMEN_DIMENSION_RULES: QimenDimensionRule[] = [
  {
    key: 'rhythm',
    label: '沟通节奏',
    direction: 'positive',
    base: 70,
    scale: 13,
    focus: '聊天反馈、见面节拍和推进速度',
    gateWeights: { 开: 2.1, 休: 1.9, 生: 1.4, 景: 0.8, 杜: -1.4, 惊: -1.0, 伤: -1.3, 死: -1.8 },
    starWeights: { 天心: 1.4, 天辅: 1.1, 天任: 0.9, 天柱: -0.9, 天芮: -1.1, 天蓬: -0.8 },
    deityWeights: { 六合: 1.2, 太阴: 0.7, 九天: 0.5, 白虎: -1.2, 玄武: -1.0, 腾蛇: -0.8 },
    flagWeights: { void: -1.2, tomb: -1.0, xing: -0.9, menPo: -1.3, horse: 0.8, zhiFu: 0.8, zhiShi: 0.7 },
  },
  {
    key: 'values',
    label: '价值观',
    direction: 'positive',
    base: 68,
    scale: 12,
    focus: '做事标准、关系边界和长期取舍',
    gateWeights: { 开: 1.2, 休: 1.1, 生: 1.4, 杜: -1.0, 惊: -0.8, 死: -1.1 },
    starWeights: { 天任: 1.6, 天心: 1.3, 天辅: 1.0, 天英: 0.6, 天芮: -1.3, 天柱: -1.0, 天蓬: -0.8 },
    deityWeights: { 值符: 1.2, 六合: 0.9, 九地: 0.8, 白虎: -1.0, 腾蛇: -0.9, 玄武: -0.8 },
    flagWeights: { void: -1.1, tomb: -0.9, xing: -0.8, menPo: -1.0, horse: 0.2, zhiFu: 1.0, zhiShi: 0.6 },
  },
  {
    key: 'stability',
    label: '稳定度',
    direction: 'positive',
    base: 69,
    scale: 12,
    focus: '关系托底能力和抗波动表现',
    gateWeights: { 生: 1.9, 休: 1.7, 开: 1.1, 杜: -1.2, 惊: -0.9, 死: -1.4, 伤: -1.0 },
    starWeights: { 天任: 1.6, 天辅: 1.0, 天禽: 0.8, 天芮: -1.2, 天冲: -0.7, 天柱: -1.0 },
    deityWeights: { 九地: 1.3, 六合: 1.0, 值符: 1.2, 白虎: -1.2, 玄武: -1.0, 腾蛇: -0.9 },
    flagWeights: { void: -1.4, tomb: -1.2, xing: -1.0, menPo: -1.2, horse: -0.1, zhiFu: 1.0, zhiShi: 0.7 },
  },
  {
    key: 'action',
    label: '行动力',
    direction: 'positive',
    base: 66,
    scale: 12,
    focus: '主动推进、执行效率和现实动作',
    gateWeights: { 开: 1.9, 生: 1.5, 景: 1.0, 休: 0.6, 杜: -1.1, 死: -1.2 },
    starWeights: { 天冲: 1.5, 天辅: 1.0, 天心: 0.9, 天柱: -0.8, 天芮: -1.0 },
    deityWeights: { 九天: 1.2, 值符: 1.0, 六合: 0.7, 白虎: -0.9, 腾蛇: -0.8, 玄武: -0.7 },
    flagWeights: { void: -1.1, tomb: -1.0, xing: -1.0, menPo: -1.1, horse: 1.2, zhiFu: 0.7, zhiShi: 0.9 },
  },
  {
    key: 'trust',
    label: '信任感',
    direction: 'positive',
    base: 67,
    scale: 12,
    focus: '安全感、坦诚度和关系可靠性',
    gateWeights: { 休: 1.8, 生: 1.5, 开: 1.1, 惊: -1.1, 伤: -1.0, 杜: -0.8, 死: -1.3 },
    starWeights: { 天心: 1.4, 天任: 1.2, 天辅: 0.8, 天蓬: -1.0, 天芮: -1.1, 天柱: -0.9 },
    deityWeights: { 太阴: 1.0, 六合: 1.2, 值符: 0.8, 玄武: -1.5, 白虎: -1.0, 腾蛇: -0.9 },
    flagWeights: { void: -1.1, tomb: -1.0, xing: -0.9, menPo: -1.2, horse: 0.1, zhiFu: 0.8, zhiShi: 0.5 },
  },
  {
    key: 'timing',
    label: '应期判断',
    direction: 'positive',
    base: 65,
    scale: 13,
    focus: '窗口期清晰度与节奏把握',
    gateWeights: { 开: 1.4, 生: 1.4, 休: 1.1, 景: 0.9, 杜: -1.0, 惊: -0.8, 死: -1.2 },
    starWeights: { 天辅: 1.1, 天任: 0.9, 天冲: 0.8, 天芮: -1.0, 天柱: -0.9 },
    deityWeights: { 九天: 0.9, 值符: 1.0, 六合: 0.8, 玄武: -0.8, 白虎: -0.9, 腾蛇: -0.7 },
    flagWeights: { void: -1.5, tomb: -1.1, xing: -0.9, menPo: -1.1, horse: 1.5, zhiFu: 0.8, zhiShi: 1.1 },
  },
  {
    key: 'resistance',
    label: '推进阻力',
    direction: 'risk',
    base: 42,
    scale: 12,
    focus: '反复、拖延、误解和中断风险',
    gateWeights: { 死: 2.0, 杜: 1.7, 惊: 1.5, 伤: 1.4, 开: -0.8, 休: -0.9, 生: -1.0 },
    starWeights: { 天芮: 1.6, 天柱: 1.4, 天蓬: 1.2, 天任: -0.7, 天心: -0.5, 天辅: -0.4 },
    deityWeights: { 白虎: 1.6, 玄武: 1.3, 腾蛇: 1.1, 六合: -0.6, 九地: -0.4, 值符: -0.5 },
    flagWeights: { void: 1.8, tomb: 1.5, xing: 1.4, menPo: 1.6, horse: 0.2, zhiFu: -0.2, zhiShi: 0.1 },
  },
  {
    key: 'grounding',
    label: '落地度',
    direction: 'positive',
    base: 68,
    scale: 12,
    focus: '从认识到落地关系的现实承接能力',
    gateWeights: { 开: 1.8, 生: 1.7, 休: 1.1, 景: 0.8, 杜: -1.1, 死: -1.3, 惊: -0.8 },
    starWeights: { 天任: 1.4, 天心: 1.0, 天辅: 1.0, 天英: 0.8, 天芮: -1.1, 天柱: -1.0 },
    deityWeights: { 值符: 1.2, 九地: 1.0, 六合: 0.9, 白虎: -1.1, 玄武: -1.0, 腾蛇: -0.9 },
    flagWeights: { void: -1.2, tomb: -1.1, xing: -0.9, menPo: -1.1, horse: 0.7, zhiFu: 1.0, zhiShi: 0.8 },
  },
];

function clampScore100(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

function toPalaceLabel(cell: PanPalaceCell | null): string {
  if (!cell) return '';
  return `${cell.trigram || '-'}${cell.position}宫`;
}

function getLevelText(score: number, direction: 'positive' | 'risk'): string {
  if (direction === 'risk') {
    if (score >= 75) return '阻力偏高';
    if (score >= 60) return '阻力偏多';
    if (score >= 45) return '阻力中位';
    return '阻力可控';
  }
  if (score >= 86) return '强势';
  if (score >= 76) return '偏强';
  if (score >= 66) return '中位';
  if (score >= 56) return '偏弱';
  return '偏低';
}

function getGradeText(score: number): string {
  if (score >= 92) return 'S';
  if (score >= 85) return 'A+';
  if (score >= 78) return 'A';
  if (score >= 70) return 'B+';
  if (score >= 62) return 'B';
  return 'C+';
}

function findDimensionScore(dimensions: QimenDimensionScore[], key: string, fallback: number): number {
  const hit = Array.isArray(dimensions) ? dimensions.find((item) => item.key === key) : null;
  if (!hit) return fallback;
  return clampScore100(Number(hit.score || 0));
}

function toStabilityText(stabilityScore: number): string {
  if (stabilityScore >= 92) return 'S+';
  if (stabilityScore >= 85) return 'A+';
  if (stabilityScore >= 76) return 'A';
  if (stabilityScore >= 68) return 'B+';
  return 'B';
}

function buildCompatibilityPercent(dimensions: QimenDimensionScore[], overallScore: number): string {
  const rhythm = findDimensionScore(dimensions, 'rhythm', overallScore);
  const values = findDimensionScore(dimensions, 'values', overallScore);
  const trust = findDimensionScore(dimensions, 'trust', overallScore);
  const grounding = findDimensionScore(dimensions, 'grounding', overallScore);
  const resistance = findDimensionScore(dimensions, 'resistance', 40);
  const transformedResistance = clampScore100(100 - resistance);
  const value = clampScore100(
    rhythm * 0.24
    + values * 0.24
    + trust * 0.2
    + grounding * 0.2
    + transformedResistance * 0.12,
  );
  return `${value}%`;
}

function calculateSingleDimension(rule: QimenDimensionRule, cells: PanPalaceCell[]): QimenDimensionScore {
  const activeCells = cells.filter((cell) => cell.position !== 5);
  if (!activeCells.length) {
    return {
      key: rule.key,
      label: rule.label,
      score: rule.direction === 'risk' ? 40 : 68,
      direction: rule.direction,
      basis: `当前盘面信息不足，先按中位估算（依据：宫位数据不完整）。`,
    };
  }

  let weightedTotal = 0;
  let weightBase = 0;
  const effects: Array<{ name: string; value: number }> = [];
  let hazardCount = 0;

  activeCells.forEach((cell) => {
    const palaceWeight = (cell.isZhiFu || cell.isZhiShi) ? 1.28 : 1;
    let local = 0;

    const gateWeight = rule.gateWeights[cell.gateSymbol] ?? 0;
    const starWeight = rule.starWeights[cell.starSymbol] ?? 0;
    const deityWeight = rule.deityWeights[cell.deitySymbol] ?? 0;
    if (gateWeight) effects.push({ name: `${cell.gateSymbol}门`, value: gateWeight });
    if (starWeight) effects.push({ name: cell.starSymbol, value: starWeight });
    if (deityWeight) effects.push({ name: cell.deitySymbol, value: deityWeight });
    local += gateWeight + starWeight + deityWeight;

    if (cell.hasVoid) {
      local += rule.flagWeights.void;
      effects.push({ name: '空亡', value: rule.flagWeights.void });
      hazardCount += 1;
    }
    if (cell.hasTomb) {
      local += rule.flagWeights.tomb;
      effects.push({ name: '入墓', value: rule.flagWeights.tomb });
      hazardCount += 1;
    }
    if (cell.hasXing) {
      local += rule.flagWeights.xing;
      effects.push({ name: '击刑', value: rule.flagWeights.xing });
      hazardCount += 1;
    }
    if (cell.hasMenPo) {
      local += rule.flagWeights.menPo;
      effects.push({ name: '门迫', value: rule.flagWeights.menPo });
      hazardCount += 1;
    }
    if (cell.isHorse) {
      local += rule.flagWeights.horse;
      effects.push({ name: '马星', value: rule.flagWeights.horse });
    }
    if (cell.isZhiFu) {
      local += rule.flagWeights.zhiFu;
      effects.push({ name: '值符', value: rule.flagWeights.zhiFu });
    }
    if (cell.isZhiShi) {
      local += rule.flagWeights.zhiShi;
      effects.push({ name: '值使', value: rule.flagWeights.zhiShi });
    }

    weightedTotal += local * palaceWeight;
    weightBase += palaceWeight;
  });

  const avg = weightBase > 0 ? weightedTotal / weightBase : 0;
  const rawScore = rule.base + avg * rule.scale;
  const score = clampScore100(Math.max(28, Math.min(98, rawScore)));
  const levelText = getLevelText(score, rule.direction);

  const sorted = effects.slice().sort((a, b) => b.value - a.value);
  const topPositive = sorted.find((item) => item.value > 0);
  const topNegative = [...sorted].reverse().find((item) => item.value < 0);
  const zhiFuCell = activeCells.find((cell) => cell.isZhiFu) || null;
  const zhiShiCell = activeCells.find((cell) => cell.isZhiShi) || null;
  const evidenceParts = [
    zhiFuCell ? `值符在${toPalaceLabel(zhiFuCell)}` : '',
    zhiShiCell ? `值使在${toPalaceLabel(zhiShiCell)}` : '',
    topPositive ? `${topPositive.name}发力` : '',
    topNegative ? `${topNegative.name}${rule.direction === 'risk' ? '缓冲' : '牵制'}` : '',
    hazardCount > 0 ? `四害触发约${hazardCount}处` : '关键宫位四害较少',
  ].filter(Boolean);

  return {
    key: rule.key,
    label: rule.label,
    score,
    direction: rule.direction,
    basis: `主看${rule.focus}，当前${levelText}（依据：${evidenceParts.join('、')}）。`,
  };
}

function buildQimenDimensionScores(resp: PanCalculateResponse): {
  dimensions: QimenDimensionScore[];
  overallScore: number;
  gradeText: string;
  compatibilityPercent: string;
  stabilityText: string;
} {
  const payload = (resp?.pan && typeof resp.pan === 'object') ? resp.pan as Record<string, unknown> : {};
  const cells = normalizePanPalaces(payload);
  const dimensions = QIMEN_DIMENSION_RULES.map((rule) => calculateSingleDimension(rule, cells));
  const positiveScores = dimensions.filter((item) => item.direction === 'positive').map((item) => item.score);
  const riskAsPositive = dimensions.filter((item) => item.direction === 'risk').map((item) => 100 - item.score);
  const allForOverall = [...positiveScores, ...riskAsPositive];
  const overallRaw = allForOverall.length
    ? allForOverall.reduce((sum, val) => sum + val, 0) / allForOverall.length
    : 72;
  const overallScore = clampScore100(overallRaw);
  const stabilityScore = findDimensionScore(dimensions, 'stability', overallScore);
  return {
    dimensions,
    overallScore,
    gradeText: getGradeText(overallScore),
    compatibilityPercent: buildCompatibilityPercent(dimensions, overallScore),
    stabilityText: toStabilityText(stabilityScore),
  };
}

function formatDimensionDigest(dimensions: QimenDimensionScore[]): string {
  if (!Array.isArray(dimensions) || !dimensions.length) return '-';
  return dimensions
    .map((item) => `${item.label}：${item.score}（${item.basis}）`)
    .join('\n');
}

function buildHookPrompt(gender: string, panSummary: string, dimensionDigest: string): string {
  const genderLabel = resolveGenderLabel(gender);
  return [
    '请基于下面这局奇门盘，写一段“灵魂伴侣开场解读”，给普通用户直接阅读。',
    '硬要求：',
    '1. 总字数控制在200到280字。',
    '2. 只输出正文，不要标题、编号、代码块。',
    '3. 用通俗口语，读起来像在说“你最近真实会遇到的情感状态”。',
    '4. 必须包含2-3句“贴近用户心理的冷读感表达”（例如：嘴上说随缘、内心怕再失望；看起来理性、其实很缺稳定回应）。',
    '5. 专业符号允许放在括号里，括号外一律说人话；不要堆术语。',
    '6. 每2-3句后都要补一个简短依据括号，括号里至少2个盘面符号，例如（依据：震宫、值符、天任星）。',
    '7. 禁止出现“安利”二字。',
    '8. 必须结合“盘面八维量化”里最强2项和最弱1项，写出“先做什么、先避开什么”。',
    '9. 参考语气：把“值符天任星落震宫、坤宫惊门动荡”翻译成“对方稳重但慢热，初见会有试探和小慌乱”，不要堆术语。',
    `用户性别：${genderLabel}`,
    '',
    '【盘面八维量化】',
    dimensionDigest,
    '',
    '【奇门盘面摘要】',
    panSummary,
  ].join('\n');
}

function buildFullPrompt(session: ZhengyuanSession): string {
  const genderLabel = resolveGenderLabel(session.gender);
  return [
    '请严格基于同一局奇门盘，输出“灵魂伴侣（正缘报告）”完整版本。',
    '硬要求：',
    '1. 必须按奇门盘逻辑推演，每一章都要有盘面依据。',
    '2. 全文中文，不低于3000字，建议3200-4200字。',
    '3. 禁止虚构具体隐私信息（姓名、具体公司、具体地址）。',
    '4. 语气要兼顾神秘感与现实行动性，但必须通俗易懂。',
    '5. 禁止输出 JSON 或代码块。',
    '6. 专业词（符号、四害、门星神干、用神等）只能写在括号内，括号外只写白话结论。',
    '7. 核心结论里只有第一句话可出现1个关键符号；后续所有章节的括号外内容禁止再出现专业术语。',
    '8. 核心结论必须是纯文本段落，禁止使用有序/无序列表。',
    '9. 除核心结论外，其余章节可保留二级小标题（###），统一使用自然段写法，不要无序列表或有序列表。',
    '10. “二、正缘画像”要含整体轮廓；“四、事业发展”只讲行业与资产；“五、相貌外表”只讲长相身高体态与穿搭，行业内容不得与相貌混写。',
    '11. 每个一级章节至少出现1个“依据括号”，每个依据括号至少含3个专业符号，格式示例：（依据：艮宫、值符、天任星、生门）。',
    '12. 重要结论请用 **加粗** 标记，便于前端做渐变强调。',
    '13. 禁止出现“安利”二字。',
    '14. 性格部分禁止出现“稳准狠”或“准、狠、稳”这类突兀词组。',
    '15. 必须新增“盘面八维”章节，按以下8个小标题逐项展开，且每项2段以上：沟通节奏、价值观、稳定度、行动力、信任感、应期判断、推进阻力、落地度。',
    '16. 原有章节信息不能删减，必须完整覆盖“你的情况、正缘画像、性格三观、事业发展、相貌外表、寻缘方向、相处状态、相处策略、百日窗口期”中的细节点。',
    '17. 若盘面八维与原有章节有重复，处理方式是“先在盘面八维给结论，再在原章节展开细节”，禁止用盘面八维替代原章节。',
    '18. 标题结构必须严格如下：',
    '# 灵魂伴侣报告',
    '## 核心结论',
    '## 盘面八维',
    '## 一、你的情况',
    '## 二、正缘画像',
    '## 三、性格三观',
    '## 四、事业发展',
    '## 五、相貌外表',
    '## 六、寻缘方向',
    '## 七、相处状态',
    '## 八、相处策略',
    '## 结语：百日窗口期',
    '',
    `用户性别：${genderLabel}`,
    '【开场解读（仅延续语气，不可照抄）】',
    session.hookText,
    '',
    '【盘面八维量化（按本局计算）】',
    formatDimensionDigest(session.dimensionScores),
    '',
    '【同一局盘面摘要】',
    session.panSummary,
  ].join('\n');
}

function normalizeResponseText(raw: string): string {
  const text = String(raw || '').trim();
  if (!text) return '';
  return text.replace(/```[\s\S]*?```/g, (block) => block.replace(/```/g, '')).trim();
}

const ZhengYuanReportPage: React.FC<{ onBack: () => void }> = () => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const sessionRef = useRef<ZhengyuanSession | null>(null);
  const startPendingRef = useRef(false);
  const unlockPendingRef = useRef(false);

  const postToIframe = (type: string, payload: Record<string, unknown> = {}) => {
    const target = iframeRef.current?.contentWindow;
    if (!target) return;
    target.postMessage({ channel: CHANNEL, type, payload }, '*');
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = (event.data && typeof event.data === 'object') ? event.data as Record<string, unknown> : null;
      if (!data || String(data.channel || '') !== CHANNEL) return;
      const type = String(data.type || '').trim();
      const payload = (data.payload && typeof data.payload === 'object') ? data.payload as Record<string, unknown> : {};

      if (type === 'start-generate') {
        if (startPendingRef.current) return;
        startPendingRef.current = true;
        const gender = String(payload.gender || '').trim();

        void (async () => {
          try {
            const req = buildRandomPanRequest();
            const panResp = await calculatePanApi(req);
            const panSummary = formatPanSummary(panResp, req);
            const qimenScoring = buildQimenDimensionScores(panResp);
            const dimensionDigest = formatDimensionDigest(qimenScoring.dimensions);
            const hookPrompt = buildHookPrompt(gender, panSummary, dimensionDigest);
            const hookResponse = await chatApi({
              feature: 'zhengyuanReport',
              featureMode: 'hook',
              messages: [{ role: 'user', content: hookPrompt }],
              knowledgeQuery: `灵魂伴侣 正缘报告 奇门盘 ${panSummary}`,
              usageQuestion: '灵魂伴侣-开场解读',
              usageExtraQuestion: panSummary,
              timeoutMs: 120000,
            });
            const hookText = normalizeResponseText(hookResponse.text);
            if (!hookText) {
              throw new Error('开场解读生成失败，请重试');
            }

            sessionRef.current = {
              gender,
              panSummary,
              sourceDateTime: String(panResp.sourceDateTime || ''),
              mode: panResp.mode === 'ke' ? 'ke' : 'hour',
              hookText,
              dimensionScores: qimenScoring.dimensions,
              overallScore: qimenScoring.overallScore,
              gradeText: qimenScoring.gradeText,
              compatibilityPercent: qimenScoring.compatibilityPercent,
              stabilityText: qimenScoring.stabilityText,
            };

            postToIframe('start-success', {
              hookText,
              panSummary,
              sourceDateTime: panResp.sourceDateTime,
              modeText: panResp.mode === 'ke' ? '刻盘' : '时盘',
              dimensions: qimenScoring.dimensions,
              overallScore: qimenScoring.overallScore,
              gradeText: qimenScoring.gradeText,
              compatibilityPercent: qimenScoring.compatibilityPercent,
              stabilityText: qimenScoring.stabilityText,
            });
          } catch (err) {
            postToIframe('start-error', {
              message: String((err as Error)?.message || '生成失败，请稍后重试'),
            });
          } finally {
            startPendingRef.current = false;
          }
        })();
        return;
      }

      if (type === 'unlock-generate') {
        if (unlockPendingRef.current) return;
        unlockPendingRef.current = true;

        void (async () => {
          try {
            const session = sessionRef.current;
            if (!session) {
              throw new Error('请先点击开始生成，完成起局后再解锁完整报告。');
            }
            const fullPrompt = buildFullPrompt(session);
            const fullResponse = await chatApi({
              feature: 'zhengyuanReport',
              featureMode: 'full',
              messages: [{ role: 'user', content: fullPrompt }],
              knowledgeQuery: `灵魂伴侣 正缘 奇门 推演 ${session.panSummary}`,
              usageQuestion: '灵魂伴侣-完整报告',
              usageExtraQuestion: session.panSummary,
              timeoutMs: 180000,
            });
            const fullReportText = normalizeResponseText(fullResponse.text);
            if (!fullReportText) {
              throw new Error('完整报告生成失败，请重试');
            }
            postToIframe('unlock-success', { fullReportText });
          } catch (err) {
            postToIframe('unlock-error', {
              message: String((err as Error)?.message || '解锁失败，请稍后重试'),
            });
          } finally {
            unlockPendingRef.current = false;
          }
        })();
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      title="奇门遁甲 - 灵魂伴侣"
      srcDoc={ZHENG_YUAN_REPORT_HTML}
      className="block h-screen w-full border-0 bg-black"
    />
  );
};

export default ZhengYuanReportPage;
